// src/pages/PasswordPages.tsx
// Routes: /forgot-password and /reset-password?token=… (the link in the
// reset email). Parents and students reset here; staff use the same flow.

import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Seo from "../components/Seo";
import { useForgotPasswordMutation, useResetPasswordMutation } from "../app/api/authApi";

const errorOf = (e: unknown, fallback: string) =>
  ((e as { data?: { error?: string } })?.data?.error) || fallback;

function Shell({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-[70vh] items-center justify-center bg-paper px-4 py-14">
      <div className="w-full max-w-md rounded-[28px] border border-[#e5e8ef] bg-white p-7 shadow-card sm:p-9">
        <img src="/images/logo.png" alt="Future Smart Lab" className="h-12 w-12 rounded-2xl" />
        <h1 className="mt-5 text-2xl font-extrabold tracking-tight text-ink">{title}</h1>
        <p className="mt-1.5 text-sm leading-6 text-ink-mute">{subtitle}</p>
        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full rounded-2xl border border-[#dde2ec] bg-white px-4 py-3 text-[15px] text-ink outline-none transition focus:border-brand-600 focus:ring-4 focus:ring-brand-100";
const buttonCls =
  "w-full rounded-2xl bg-brand-600 py-3 text-[15px] font-bold text-white shadow-soft transition hover:bg-brand-700 disabled:opacity-60";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [send, { isLoading }] = useForgotPasswordMutation();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return setError("Enter the email you signed up with.");
    try {
      await send({ email: email.trim().toLowerCase() }).unwrap();
      setSent(true);
    } catch (err) {
      setError(errorOf(err, "Something went wrong. Please try again."));
    }
  };

  return (
    <>
      <Seo title="Forgot password" description="Reset your Future Smart Lab password." path="/forgot-password" noindex />
      <Shell
        title={sent ? "Check your email" : "Forgot your password?"}
        subtitle={
          sent
            ? `If ${email.trim()} has an account, a reset link is on its way. It works for one hour. Check your spam folder too.`
            : "Enter the email you signed up with and we'll send you a link to choose a new password."
        }
      >
        {sent ? (
          <div className="space-y-4">
            <Link to="/login" className={`${buttonCls} block text-center`}>
              Back to sign in
            </Link>
            <p className="rounded-2xl bg-[#f1f3f8] px-4 py-3 text-xs leading-5 text-ink-mute">
              Nothing after a few minutes? Make sure it's the email you signed up with. No account yet?{" "}
              <Link to="/register" className="font-semibold text-brand-600 hover:underline">Create one</Link>.{" "}
              <button type="button" onClick={() => setSent(false)} className="font-semibold text-brand-600 hover:underline">
                Try another email
              </button>
            </p>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="space-y-4">
            <label className="block text-sm font-semibold text-ink-soft">
              Email address
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className={`${inputCls} mt-1.5`}
              />
            </label>
            {error && <p className="text-sm font-semibold text-[#b94a3a]">{error}</p>}
            <button type="submit" disabled={isLoading} className={buttonCls}>
              {isLoading ? "Sending…" : "Send reset link"}
            </button>
            <p className="text-center text-sm text-ink-mute">
              Remembered it? <Link to="/login" className="font-semibold text-brand-600 hover:underline">Sign in</Link>
            </p>
            <p className="rounded-2xl bg-[#f1f3f8] px-4 py-3 text-xs leading-5 text-ink-mute">
              Learners aged 9–12 with a username: ask your parent to set a new password on your Learn page.
            </p>
          </form>
        )}
      </Shell>
    </>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [reset, { isLoading }] = useResetPasswordMutation();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 6) return setError("Use at least 6 characters.");
    if (password !== confirm) return setError("The two passwords don't match.");
    try {
      await reset({ token, password }).unwrap();
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      setError(errorOf(err, "That link has expired or was already used. Ask for a new one."));
    }
  };

  if (!token) {
    return (
      <Shell title="This link isn't complete" subtitle="Open the link from your reset email again, or ask for a new one.">
        <Link to="/forgot-password" className={`${buttonCls} block text-center`}>Send me a new link</Link>
      </Shell>
    );
  }

  return (
    <>
      <Seo title="Choose a new password" description="Choose a new Future Smart Lab password." path="/reset-password" noindex />
      <Shell
        title={done ? "Password changed" : "Choose a new password"}
        subtitle={done ? "Taking you to sign in…" : "Use at least 6 characters, something you'll remember but others can't guess."}
      >
        {!done && (
          <form onSubmit={submit} noValidate className="space-y-4">
            <label className="block text-sm font-semibold text-ink-soft">
              New password
              <input
                type={show ? "text" : "password"}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`${inputCls} mt-1.5`}
              />
            </label>
            <label className="block text-sm font-semibold text-ink-soft">
              Type it again
              <input
                type={show ? "text" : "password"}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className={`${inputCls} mt-1.5`}
              />
            </label>
            <label className="flex items-center gap-2 text-sm text-ink-mute">
              <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} className="accent-brand-600" />
              Show passwords
            </label>
            {error && (
              <p className="text-sm font-semibold text-[#b94a3a]">
                {error}{" "}
                {/expired|used/i.test(error) && <Link to="/forgot-password" className="underline">Get a new link</Link>}
              </p>
            )}
            <button type="submit" disabled={isLoading} className={buttonCls}>
              {isLoading ? "Saving…" : "Save new password"}
            </button>
          </form>
        )}
      </Shell>
    </>
  );
}
